/**
 * Flow Template Registry
 *
 * Provides access to pre-built flow templates for common automation patterns.
 */

import { readFileSync, readdirSync, existsSync } from "fs";
import { join, dirname } from "path";
import { fileURLToPath } from "url";
import type { FlowDefinition, ConnectionReference } from "../api/types.js";

const __filename = fileURLToPath(import.meta.url);
const __dirname = dirname(__filename);

// Templates are stored in project root /templates directory
const TEMPLATES_DIR = join(__dirname, "..", "..", "templates");

// ============================================================================
// Types
// ============================================================================

export interface TemplateParameter {
  type: "string" | "number" | "boolean" | "array" | "object";
  description: string;
  required: boolean;
  default?: unknown;
  enum?: string[];
}

export interface TemplateConnectionRef {
  connectorId: string;
  tier: "Standard" | "Premium" | "NotSpecified";
  description: string;
}

export interface FlowTemplate {
  /** Unique template ID */
  id: string;
  /** Display name */
  name: string;
  /** Full description */
  description: string;
  /** Category for grouping */
  category: string;
  /** Searchable tags */
  tags: string[];
  /** Parameters to customize the template */
  parameters: Record<string, TemplateParameter>;
  /** Required connection references */
  connectionReferences: Record<string, TemplateConnectionRef>;
  /** The flow definition */
  definition: FlowDefinition;
}

export interface TemplateListItem {
  id: string;
  name: string;
  description: string;
  category: string;
  tags: string[];
  requiredConnectors: string[];
}

// ============================================================================
// Template Loading
// ============================================================================

const templateCache = new Map<string, FlowTemplate>();

/**
 * Load a template from JSON file
 */
function loadTemplate(filePath: string): FlowTemplate | null {
  try {
    const content = readFileSync(filePath, "utf-8");
    const data = JSON.parse(content) as FlowTemplate;
    return data;
  } catch {
    return null;
  }
}

/**
 * Initialize template cache by loading all JSON templates
 */
function initializeCache(): void {
  if (templateCache.size > 0) return;

  if (!existsSync(TEMPLATES_DIR)) {
    return;
  }

  const files = readdirSync(TEMPLATES_DIR).filter(
    (f) => f.endsWith(".json") && !f.startsWith("_")
  );

  for (const file of files) {
    const template = loadTemplate(join(TEMPLATES_DIR, file));
    if (template?.id) {
      templateCache.set(template.id, template);
    }
  }
}

// ============================================================================
// Public API
// ============================================================================

/**
 * Get all available templates as list items (without full definitions)
 */
export function listTemplates(): TemplateListItem[] {
  initializeCache();

  return Array.from(templateCache.values()).map((t) => ({
    id: t.id,
    name: t.name,
    description: t.description,
    category: t.category,
    tags: t.tags,
    requiredConnectors: Object.keys(t.connectionReferences),
  }));
}

/**
 * Get templates by category
 */
export function getTemplatesByCategory(category: string): TemplateListItem[] {
  return listTemplates().filter(
    (t) => t.category.toLowerCase() === category.toLowerCase()
  );
}

/**
 * Search templates by keyword
 */
export function searchTemplates(query: string): TemplateListItem[] {
  const q = query.toLowerCase();
  return listTemplates().filter(
    (t) =>
      t.name.toLowerCase().includes(q) ||
      t.description.toLowerCase().includes(q) ||
      t.tags.some((tag) => tag.toLowerCase().includes(q))
  );
}

/**
 * Get a specific template by ID
 */
export function getTemplate(id: string): FlowTemplate | null {
  initializeCache();
  return templateCache.get(id) ?? null;
}

/**
 * Get all template categories
 */
export function getCategories(): string[] {
  initializeCache();
  const categories = new Set<string>();
  for (const template of templateCache.values()) {
    categories.add(template.category);
  }
  return Array.from(categories).sort();
}

/**
 * Instantiate a template with parameter values
 */
export function instantiateTemplate(
  templateId: string,
  parameters: Record<string, unknown>,
  connectionMappings: Record<string, string>
): {
  definition: FlowDefinition;
  connectionReferences: Record<string, ConnectionReference>;
} | null {
  const template = getTemplate(templateId);
  if (!template) return null;

  // Validate required parameters
  for (const [key, param] of Object.entries(template.parameters)) {
    if (param.required && parameters[key] === undefined) {
      throw new Error(`Missing required parameter: ${key}`);
    }
  }

  // Deep clone definition
  const definition = JSON.parse(
    JSON.stringify(template.definition)
  ) as FlowDefinition;

  // Build connection references with user's connection names
  const connectionReferences: Record<string, ConnectionReference> = {};
  for (const [refKey, refDef] of Object.entries(template.connectionReferences)) {
    const connectionName = connectionMappings[refKey];
    if (!connectionName) {
      throw new Error(`Missing connection mapping for: ${refKey}`);
    }
    connectionReferences[refKey] = {
      connectionName,
      id: refDef.connectorId,
      source: "Embedded",
      tier: refDef.tier,
    };
  }

  return { definition, connectionReferences };
}

/**
 * Get template parameter schema for validation
 */
export function getTemplateParameterSchema(
  templateId: string
): Record<string, TemplateParameter> | null {
  const template = getTemplate(templateId);
  return template?.parameters ?? null;
}

/**
 * Get required connectors for a template
 */
export function getRequiredConnectors(
  templateId: string
): Array<TemplateConnectionRef & { key: string }> | null {
  const template = getTemplate(templateId);
  if (!template) return null;
  return Object.entries(template.connectionReferences).map(([key, ref]) => ({
    ...ref,
    key,
  }));
}

// ============================================================================
// Built-in Template IDs (for convenience)
// ============================================================================

export const TEMPLATE_IDS = {
  APPROVAL: "approval-workflow",
  NOTIFICATION: "multi-channel-notification",
  DATA_SYNC: "data-sync",
  ERROR_HANDLER: "error-handler",
} as const;
